import * as Sentry from "@sentry/browser"
import * as SentryVue from "@sentry/vue"
import { Platform } from "quasar"
import { boot } from "quasar/wrappers"
import { Log } from "src/util/classes/log"

const DSN_KEY = "https://80879f6b71da4034b2f0b01137c38998@o33876.ingest.sentry.io/5731323"
const serializationDepth = 4
const environment = determineEnvironment()
const maxReplayDuration = 300000 // 5 minutes

const ignoredErrorList = [
  "setOptions failed", // Internal issue with googles media handler https://github.com/zxing-js/ngx-scanner/issues/297
  "setPhotoOptions failed",
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded", // Couldn't get the animation frame in time. Not important https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
  "Using addListener() without 'await' is deprecated.", // Capacitor developers being bad!
  // TODO: We shouldn't really ignore those, but we need to tinker with DB stuff. For now this should be safe to ignore.
  "Another connection wants to delete database", // Triggered when we do sfx.nukeEverything whilst we have the DB open
]

export default boot(async ({ app, router }) => {
  if (process.env.MODE === "capacitor") {
    const SentryCapacitor = await import("@src-capacitor/@sentry/capacitor")
    SentryCapacitor.init(
      {
        dsn: DSN_KEY,
      },
      SentryVue.init
    )
  }

  SentryVue.init({
    app,
    dsn: DSN_KEY,
    environment,
    normalizeDepth: serializationDepth,

    // Put all the end points that we communicate with here
    tracePropagationTargets: ["localhost", /^\//],

    // Allow for offline caching of requests
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

    integrations: [
      // For some reason the typings are off, however, the official guide is using the same code
      // Additionally, there are no reports of this being broken.
      // @ts-expect-error: Typings of router and sentry expectations don't match
      Sentry.browserTracingIntegration({ router }),

      Sentry.feedbackIntegration({ autoInject: false }),

      SentryVue.captureConsoleIntegration({
        // array of methods that should be captured
        // defaults to ['log', 'info', 'warn', 'error', 'debug', 'assert']
        levels: ["warn", "error", "info"],
      }),

      SentryVue.extraErrorDataIntegration({
        // Limit of how deep the object serializer should go. Anything deeper than limit will
        // be replaced with standard Node.js REPL notation of [Object], [Array], [Function] or
        // a primitive value. Defaults to 3.
        // When changing this value, make sure to update `normalizeDepth` of the whole SDK
        // to `depth + 1` in order to get it serialized properly - https://docs.sentry.io/platforms/javascript/configuration/options/#normalize-depth
        depth: serializationDepth,
      }),

      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: true,
        mutationLimit: 1000,
        maxReplayDuration,
      }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1,

    trackComponents: true,

    ignoreErrors: ignoredErrorList,

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1,
  })

  // Handle unhandled Vue specific errors
  app.config.errorHandler = (error) => {
    const errorStr = error?.toString()
    if (errorStr && ignoreErrors(errorStr)) return

    Log.error({ message: "Unhandled Vue error", error })
  }

  // Handle unhandled non-Vue specific errors
  window.addEventListener("error", (event) => {
    if (ignoreErrors(event.message)) return

    Log.error({ message: "Unhandled error", error: event.error, data: { event } })
  })
})

/**
 * Function for determining the Sentry environment.
 *
 * NOTE: This function has a sibling doing the same job in `quasar.config.js` for builds.
 * If this is updated, please also update the sibling.
 */
function determineEnvironment() {
  const isDev = process.env.DEV ? "-dev" : "-prod"
  const mode = getMode()
  return mode + isDev
}

function getMode() {
  if (process.env.MODE === "capacitor") {
    if (Platform.is.android) return "android"
    if (Platform.is.ios) return "ios"
  }
  return process.env.MODE ?? "unknown_environment"
}

function ignoreErrors(currentError: string) {
  for (const error of ignoredErrorList) {
    if (currentError.startsWith(error)) return true
  }
}
